import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Linkedin, ArrowUpRight, Heart, Coffee, ExternalLink } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { trackEvent } from '../lib/analytics';

const CrescentsHoverLink = ({ t, children }) => (
  <HoverCard openDelay={80} closeDelay={80}>
    <HoverCardTrigger asChild>
      <a
        href="https://crescents.ma"
        target="_blank"
        rel="noopener noreferrer"
        title="Agence Web Tanger"
        aria-label="Agence Web Tanger"
        className="text-[#00FFD1] hover:text-white transition-colors duration-300 font-medium underline-offset-4 hover:underline"
        data-testid="footer-crescents-link"
      >
        {children}
      </a>
    </HoverCardTrigger>
    <HoverCardContent
      align="center"
      sideOffset={8}
      className="w-64 bg-[#0a0a0a] border border-white/10 text-white p-4 shadow-xl"
      data-testid="footer-crescents-hovercard"
    >
      <p className="text-sm font-semibold text-white">Agence Web Tanger</p>
      <p className="mt-1 text-xs text-white/50 leading-relaxed">{t('footer.agencyTagline')}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs text-[#00FFD1]">
        crescents.ma
        <ExternalLink className="w-3 h-3" />
      </span>
    </HoverCardContent>
  </HoverCard>
);

const Footer = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const langPrefix = i18n.language && i18n.language !== 'en' ? `/${i18n.language}` : '';

  const navItems = [
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.process'), href: '#process' },
    { label: t('nav.whyMe'), href: '#why-me' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#contact' },
  ];

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative py-16 bg-black border-t border-white/10" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a
              href="#"
              onClick={handleScrollToTop}
              className="inline-block text-2xl font-semibold text-white hover:text-[#00FFD1] transition-colors duration-300 mb-4"
            >
              <span className="text-[#00FFD1]">&lt;</span>
              OBN
              <span className="text-[#00FFD1]">/&gt;</span>
            </a>
            <p className="text-white/50 text-sm max-w-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://www.linkedin.com/in/oualidbennaid/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 hover:bg-[#00FFD1]/10 hover:border-[#00FFD1]/30 transition-all duration-300"
                aria-label="LinkedIn"
                data-testid="footer-linkedin-link"
              >
                <Linkedin className="w-4 h-4 text-white/60 hover:text-[#00FFD1]" />
              </a>
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">
              {t('footer.navigation')}
            </h4>
            <nav className="space-y-3">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="block text-white/50 text-sm hover:text-[#00FFD1] transition-colors duration-300"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact CTA */}
          <div>
            <h4 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">
              {t('footer.getInTouch')}
            </h4>
            <a
              href="#contact"
              onClick={() => trackEvent('book_call_click', { location: 'footer' })}
              className="inline-flex items-center gap-2 text-[#00FFD1] hover:text-white transition-colors duration-300 text-sm mb-4"
            >
              <span>{t('footer.scheduleCall')}</span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="text-white/40 text-sm mb-2">{t('footer.email')}</p>
            <a
              href="mailto:oualid@bennaid.com"
              className="text-white/60 text-sm hover:text-[#00FFD1] transition-colors duration-300"
            >
              oualid@bennaid.com
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-sm">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-6">
            <button
              onClick={() => window.dispatchEvent(new Event('open-cookie-consent'))}
              className="text-white/30 text-sm hover:text-white/60 transition-colors duration-300"
              data-testid="footer-cookie-settings"
            >
              {t('consent.manage')}
            </button>
            <Link
              to={`${langPrefix}/privacy`}
              data-testid="footer-privacy-link"
              className="text-white/30 text-sm hover:text-white/60 transition-colors duration-300"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to={`${langPrefix}/terms`}
              data-testid="footer-terms-link"
              className="text-white/30 text-sm hover:text-white/60 transition-colors duration-300"
            >
              {t('footer.terms')}
            </Link>
          </div>
        </div>

        {/* Designer credit */}
        <p
          className="pt-6 text-center text-white/30 text-sm"
          data-testid="footer-designed-by"
        >
          <Trans
            i18nKey="footer.designedBy"
            components={{
              heart: (
                <Heart className="inline-block w-4 h-4 mx-0.5 align-middle text-red-500 fill-red-500" aria-hidden="true" />
              ),
              coffee: (
                <Coffee className="inline-block w-4 h-4 mx-0.5 align-middle text-amber-500" aria-hidden="true" />
              ),
              a: <CrescentsHoverLink t={t} />,
            }}
          />
        </p>
      </div>
    </footer>
  );
};

export default Footer;
